import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { openBugReportModal } from "../services/bugreport";
import { getSidebarPanel, type SidebarPanel } from "../services/settings";
import { usePortalPermissions } from "../hooks/usePortalPermissions";

export type SidebarRole = "admin" | "manager" | "employee";

interface NavItem {
 label: string;
 to?: string;
 onClick?: () => void;
 icon: React.ReactNode;
 /** Portal module code — if set, item is hidden when user lacks access */
 moduleCode?: string;
}

interface NavSection {
 /** Visible header above the items in this group. */
 title: string;
 items: NavItem[];
 /** If true, the section is rendered as a collapsible accordion. */
 collapsible?: boolean;
 /** Default collapsed state (only relevant when collapsible is true). */
 defaultCollapsed?: boolean;
}

interface SidebarProps {
 role: SidebarRole;
 userId?: string;
 collapsed?: boolean;
 /** Mobile drawer open state - controlled by parent */
 mobileOpen?: boolean;
 /** Callback to close mobile drawer */
 onMobileClose?: () => void;
}

interface SidebarHeaderLogoProps {
 className?: string;
 compact?: boolean;
}

const LOGO_EXPANDED ="https://cpupload.kfintech.com/ChannelServices/Images/kfintech-logo.png";
const LOGO_COLLAPSED ="https://tse4.mm.bing.net/th/id/OIP.FBHr2yrRo_GJgIEb4Vr_TQHaGb?r=0&w=1548&h=1344&rs=1&pid=ImgDetMain&o=7&rm=3";

function SidebarHeaderLogo({
 className ="",
 compact = false,
}: SidebarHeaderLogoProps) {
 const [hasError, setHasError] = useState(false);

 if (hasError) {
 return (
 <svg
 className={`text-slate-600 ${className}`}
 viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="9"/>
 <path d="M12 8v4l3 3"/>
 </svg>
 );
 }

 return (
 <img
 src={compact ? LOGO_COLLAPSED : LOGO_EXPANDED}
 alt="Company logo"onError={() => setHasError(true)}
 className={`h-auto object-contain transition-all duration-200 ${
 compact ?"h-6 w-6":"max-w-25"} ${className}`}
 style={{ background: "transparent"}}
 loading="eager"/>
 );
}

// Unified green→blue brand accent applied to every active nav item, regardless
// of role. Keep the`Record<SidebarRole, ...>`shape so the rest of the
// component can stay role-agnostic.
const ROLE_META: Record<
 SidebarRole,
 {
 activeBg: string;
 activeText: string;
 activeRing: string;
 dotColor: string;
 }
> = {
 admin: {
 activeBg: "bg-gradient-brand-10",
 activeText: "text-role-primary",
 activeRing: "ring-emerald-200",
 dotColor: "bg-gradient-brand",
 },
 manager: {
 activeBg: "bg-gradient-brand-10",
 activeText: "text-role-primary",
 activeRing: "ring-emerald-200",
 dotColor: "bg-gradient-brand",
 },
 employee: {
 activeBg: "bg-gradient-brand-10",
 activeText: "text-role-primary",
 activeRing: "ring-emerald-200",
 dotColor: "bg-gradient-brand",
 },
};

/* --------------------------------------------------------------------------
 * Icon library
 *
 * Each path gets a distinct icon — no duplicates across the sidebar. Icons
 * deliberately mix outlined geometry (circles, polygons, paths) so adjacent
 * items don't look like the same shape repeated.
 * ------------------------------------------------------------------------ */

const Icon = ({ children }: { children: React.ReactNode }) => (
 <svg
 className="h-5 w-5"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="1.7"strokeLinecap="round"strokeLinejoin="round">
 {children}
 </svg>
);

const ICONS = {
 // Overview / dashboard — 4-square grid
 dashboard: (
 <Icon>
 <rect x="3"y="3"width="7"height="9"rx="1.5"/>
 <rect x="14"y="3"width="7"height="5"rx="1.5"/>
 <rect x="14"y="12"width="7"height="9"rx="1.5"/>
 <rect x="3"y="16"width="7"height="5"rx="1.5"/>
 </Icon>
 ),
 // Projects (all) — stacked folder
 folderStack: (
 <Icon>
 <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
 <path d="M2 13h20"/>
 </Icon>
 ),
 // My projects (manager) — briefcase
 briefcase: (
 <Icon>
 <rect x="2"y="7"width="20"height="14"rx="2"/>
 <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
 <path d="M2 13h20"/>
 </Icon>
 ),
 // All employees — group of people
 team: (
 <Icon>
 <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
 <circle cx="9"cy="7"r="4"/>
 <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
 <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
 </Icon>
 ),
 // Add employee — single user + plus
 userPlus: (
 <Icon>
 <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
 <circle cx="9"cy="7"r="4"/>
 <path d="M19 8v6M22 11h-6"/>
 </Icon>
 ),
 // Assign manager — crown above figure (signals authority/leadership)
 crown: (
 <Icon>
 <path d="M3 18l3-9 4 6 2-9 2 9 4-6 3 9"/>
 <path d="M3 18h18"/>
 </Icon>
 ),
 // Assign employee — link / chain (signals connection/assignment)
 link: (
 <Icon>
 <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
 <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
 </Icon>
 ),
 // Work logs (admin/manager view) — clipboard with clock
 clipboardClock: (
 <Icon>
 <rect x="5"y="4"width="14"height="17"rx="2"/>
 <path d="M9 4h6v3H9z"/>
 <circle cx="12"cy="14"r="3.2"/>
 <path d="M12 12.5v1.7L13.2 15"/>
 </Icon>
 ),
 // Timesheets — document / clipboard list
 timesheet: (
 <Icon>
 <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
 <polyline points="14 2 14 8 20 8"/>
 <line x1="16"y1="13"x2="8"y2="13"/>
 <line x1="16"y1="17"x2="8"y2="17"/>
 <polyline points="10 9 9 9 8 9"/>
 </Icon>
 ),
 // Audit logs — shield + check (system trust)
 shieldCheck: (
 <Icon>
 <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
 <path d="M9 12l2 2 4-4"/>
 </Icon>
 ),
 // Pending leaves — hourglass over palm leaf
 hourglass: (
 <Icon>
 <path d="M6 2h12M6 22h12"/>
 <path d="M6 2l6 7 6-7"/>
 <path d="M6 22l6-7 6 7"/>
 </Icon>
 ),
 // Pending work logs — inbox with arrow
 inboxArrow: (
 <Icon>
 <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
 <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
 <path d="M12 11V3M9 6l3-3 3 3"/>
 </Icon>
 ),
 // Invoice — receipt / document
 receipt: (
 <Icon>
 <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
 <polyline points="14 2 14 8 20 8"/>
 <line x1="16"y1="13"x2="8"y2="13"/>
 <line x1="16"y1="17"x2="8"y2="17"/>
 <polyline points="10 9 9 9 8 9"/>
 <path d="M10 13l2 2 4-4"/>
 </Icon>
 ),
 // Issues / alerts — warning triangle
 triangleAlert: (
 <Icon>
 <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
 <path d="M12 9v4M12 17h.01"/>
 </Icon>
 ),
 // Team work logs (manager) — bar chart trend
 trend: (
 <Icon>
 <path d="M3 3v18h18"/>
 <path d="M7 14l3-3 3 3 5-6"/>
 <path d="M14 8h4v4"/>
 </Icon>
 ),
 // My tasks — checkbox stack
 checks: (
 <Icon>
 <path d="M8 6l3 3 8-8"/>
 <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
 <path d="M8 14l3 3 8-8"/>
 </Icon>
 ),
 // Weekly log — 5-cell calendar grid
 weekly: (
 <Icon>
 <rect x="3"y="4"width="18"height="16"rx="2"/>
 <path d="M3 9h18M8 4v16M13 4v16M18 4v16"/>
 </Icon>
 ),
 // Work calendar — calendar with stacked blocks (cascading)
 cascadeCal: (
 <Icon>
 <rect x="3"y="5"width="18"height="16"rx="2"/>
 <path d="M3 10h18M8 3v4M16 3v4"/>
 <rect x="6"y="12"width="6"height="3"rx="0.6"/>
 <rect x="9"y="15"width="6"height="3"rx="0.6"/>
 </Icon>
 ),
 // Calendar (My leaves) — calendar dot
 calendar: (
 <Icon>
 <rect x="3"y="5"width="18"height="16"rx="2"/>
 <path d="M3 9h18M8 3v4M16 3v4"/>
 <circle cx="12"cy="14"r="1.5"/>
 </Icon>
 ),
 // Apply for leave — sun over horizon (vacation)
 sun: (
 <Icon>
 <circle cx="12"cy="12"r="4"/>
 <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>
 </Icon>
 ),
 // Submit work log — pencil writing
 pencil: (
 <Icon>
 <path d="M12 20h9"/>
 <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/>
 </Icon>
 ),
 // My work logs — open book / journal
 journal: (
 <Icon>
 <path d="M4 4h11a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H4z"/>
 <path d="M4 4v16"/>
 <path d="M8 9h6M8 13h6"/>
 </Icon>
 ),
 // Issues (employee) — speech bubble + exclamation
 speechAlert: (
 <Icon>
 <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
 <path d="M12 8v4M12 16h.01"/>
 </Icon>
 ),
 // Sign out — arrow leaving box
 signOut: (
 <Icon>
 <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
 <path d="M16 17l5-5-5-5M21 12H9"/>
 </Icon>
 ),
 // Settings — gear / cog
 settings: (
 <Icon>
 <circle cx="12"cy="12"r="3"/>
 <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
 </Icon>
 ),
 // Profile — user silhouette
 profile: (
 <Icon>
 <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
 <circle cx="12"cy="7"r="4"/>
 </Icon>
 ),
 // Bug — beetle silhouette (Report a bug)
 bug: (
 <Icon>
 <rect x="8"y="6"width="8"height="14"rx="4"/>
 <path d="M12 2v4M9 9l-5-3M15 9l5-3M9 15l-5 3M15 15l5 3"/>
 </Icon>
 ),
 // Bug list — clipboard with bug
 bugList: (
 <Icon>
 <rect x="6"y="4"width="12"height="16"rx="2"/>
 <path d="M9 4h6v2H9z"/>
 <circle cx="12"cy="13"r="2"/>
 <path d="M12 9v2M12 15v2M9 13H7M17 13h-2"/>
 </Icon>
 ),
 // Import & Export — upload arrow
 importExport: (
 <Icon>
 <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
 <polyline points="17 8 12 3 7 8"/>
 <line x1="12"y1="3"x2="12"y2="15"/>
 </Icon>
 ),
 // Change password — key + shield combo
 key: (
 <Icon>
 <circle cx="8"cy="15"r="4"/>
 <path d="M10.85 12.15 19 4M18 5l3 3M15 8l3 3"/>
 </Icon>
 ),
 // Home — house silhouette
 home: (
 <Icon>
 <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
 <polyline points="9 22 9 12 15 12 15 22"/>
 </Icon>
 ),
 // Reports — bar chart
 reports: (
 <Icon>
 <rect x="3"y="12"width="4"height="9"rx="1"/>
 <rect x="10"y="7"width="4"height="14"rx="1"/>
 <rect x="17"y="3"width="4"height="18"rx="1"/>
 </Icon>
 ),
 // Milestones — flag on a pole
 milestone: (
 <Icon>
 <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
 <line x1="4"y1="22"x2="4"y2="15"/>
 </Icon>
 ),
 // Collaboration — two speech bubbles
 collaboration: (
 <Icon>
 <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
 <path d="M8 9h8M8 13h4"/>
 </Icon>
 ),
 // Team Review — users with checkmark (for approving team worklogs)
 teamReview: (
 <Icon>
 <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
 <circle cx="9"cy="7"r="4"/>
 <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
 <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
 <path d="M17 11l2 2 4-4"/>
 </Icon>
 ),
};

export default function Sidebar({
 role,
 userId,
 collapsed = false,
 mobileOpen = false,
 onMobileClose,
}: SidebarProps) {
 const navigate = useNavigate();
 const location = useLocation();
 const [collapsedSections, setCollapsedSections] = useState<
 Record<string, boolean>
 >({});
 const [panel, setPanel] = useState<SidebarPanel>(getSidebarPanel);

 // Handle ESC key to close mobile drawer
 const handleEscKey = useCallback(
 (e: KeyboardEvent) => {
 if (e.key ==="Escape"&& mobileOpen) {
 onMobileClose?.();
 }
 },
 [mobileOpen, onMobileClose],
 );

 // Add/remove ESC listener and body scroll lock
 useEffect(() => {
 if (mobileOpen) {
 document.addEventListener("keydown", handleEscKey);
 document.body.classList.add("sidebar-open");
 } else {
 document.body.classList.remove("sidebar-open");
 }
 return () => {
 document.removeEventListener("keydown", handleEscKey);
 document.body.classList.remove("sidebar-open");
 };
 }, [mobileOpen, handleEscKey]);

 // Listen for settings changes (from the Settings page)
 useEffect(() => {
 const handler = () => setPanel(getSidebarPanel());
 window.addEventListener("tt:settings-changed", handler);
 window.addEventListener("storage", handler);
 return () => {
 window.removeEventListener("tt:settings-changed", handler);
 window.removeEventListener("storage", handler);
 };
 }, []);

 // Panel-dependent styling
 const panelStyles = useMemo(() => {
 return {
 bg: "bg-white",
 border: "border-slate-200",
 text: "text-gray-600",
 mutedText: "text-gray-500",
 hoverBg: "hover:bg-slate-100",
 activeText: "text-role-primary",
 activeBg: "bg-gradient-brand-10",
 };
 }, [panel]);

 const isDarkPanel = panel !=="light";

 const meta = ROLE_META[role];

 // Portal permissions — controls which nav items are visible per role
 // Admin always sees everything. Others filtered by Admin Setup page.
 const { canAccess, canDo } = usePortalPermissions();

 const hasUserId = Boolean(userId && /^\d+$/.test(userId));

 // Build a path that depends on the logged-in id. If we somehow don't have
 // one (shouldn't happen post-login), fall back to a no-op so the UI doesn't
 // navigate to an invalid URL.
 const ownPath = (suffix: (id: string) => string): string =>
 hasUserId ? suffix(userId as string) :"#";

 const sections: NavSection[] = useMemo(() => {
 // Single unified nav list — visibility controlled entirely by Portal Configuration.
 // canAccess() (from usePortalPermissions) determines which items each role can see.
 const mainItems: NavItem[] = [
 {
 label: "Dashboard",
 to: "/dashboard",
 icon: ICONS.dashboard,
 moduleCode: "DASHBOARD",
 },
 {
 label: "Projects",
 to: "/projects",
 icon: ICONS.folderStack,
 moduleCode: "PROJECTS",
 },
 { label: "Users", to: "/users", icon: ICONS.team, moduleCode: "USERS"},
 {
 label: "Worklogs",
 to: ownPath((id) =>`/employees/${id}/worklogs`),
 icon: ICONS.weekly,
 moduleCode: "WORKLOG",
 },
 {
 label: "Timesheets",
 to: "/timesheets",
 icon: ICONS.timesheet,
 moduleCode: "TIMESHEETS",
 },
 {
 label: "Leaves",
 to: "/leaves",
 icon: ICONS.calendar,
 moduleCode: "LEAVES",
 },
 {
 label: "Allocations",
 to: "/allocations",
 icon: ICONS.reports,
 },
 {
 label: "Migrations",
 to: "/admin/migrations",
 icon: ICONS.importExport,
 moduleCode: "MIGRATIONS",
 },
 ];

 return [
 { title: "", items: mainItems },
 {
 title: "Help",
 items: [
 {
 label: "Report a bug",
 onClick: openBugReportModal,
 icon: ICONS.bug,
 },
 ],
 },
 ];
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [role, userId]);

 // Filter based on portal permissions from Admin Setup page.
 // canAccess checks can_read for the module — if disabled, item is hidden.
 const filteredSections: NavSection[] = useMemo(() => {
 return sections
 .map((s) => ({
 ...s,
 items: s.items.filter((item) => {
 if (!item.moduleCode) return true; // Help items, etc.
 // Special case: Team Worklogs requires approve permission on WORKLOG module
 if (item.moduleCode ==="WORKLOG_APPROVE") {
 return canDo("WORKLOG","approve");
 }
 return canAccess(item.moduleCode);
 }),
 }))
 .filter((s) => s.items.length > 0);
 }, [sections, canAccess, canDo]);

 // Flat list across all sections, used to pick the most-specific active item.
 const flatItems: NavItem[] = useMemo(
 () => filteredSections.flatMap((s) => s.items),
 [filteredSections],
 );

 // Pick the single most-specific sidebar entry that matches the current path.
 // Without this, a parent path like /admin/employees would also light up while
 // visiting /admin/employees/new, because both would pass startsWith().
 const activeTo = useMemo(() => {
 // Employee-specific timelog pages are reached from "Team work logs" for
 // managers, so keep that sidebar item highlighted.
 if (
 role ==="manager"&&
 /^\/employees\/\d+\/timelogs$/.test(location.pathname)
 ) {
 return"/manager/timelogs";
 }

 const dashboardOnly = new Set(["/dashboard","/admin","/manager","/employee",
 ]);
 let best: string | null = null;
 for (const item of flatItems) {
 const to = item.to;
 if (!to || to ==="#") continue;
 if (to === location.pathname) return to;
 if (dashboardOnly.has(to)) continue;
 const matchesPrefix =
 location.pathname.startsWith(to) &&
 (location.pathname.length === to.length ||
 location.pathname[to.length] ==="/");
 if (matchesPrefix && (!best || to.length > best.length)) {
 best = to;
 }
 }
 return best;
 }, [flatItems, location.pathname, role]);

 const isActive = (to?: string) => Boolean(to) && to === activeTo;

 const renderItem = (item: NavItem) => {
 const active = isActive(item.to);
 const disabled = item.to ==="#";
 const click = () => {
 if (disabled) return;
 onMobileClose?.();
 if (item.to) navigate(item.to);
 else item.onClick?.();
 };
 return (
 <button
 key={item.label}
 type="button"onClick={click}
 disabled={disabled}
 title={
 collapsed
 ? disabled
 ?"Sign in to access this.": item.label
 : disabled
 ?"Sign in to access this.": undefined
 }
 className={`group flex w-full items-center rounded-[10px] text-sm font-medium transition-all duration-150 ${
 collapsed ?"h-11 justify-center px-2":"h-11 gap-3 px-3"} ${
 disabled
 ?"cursor-not-allowed opacity-40": active
 ?`${panelStyles.activeBg} ring-1 ring-inset ring-white/10`:`${panelStyles.text} ${panelStyles.hoverBg} hover:translate-x-0.5`}`}
 style={active && !disabled ? { color: "var(--color-primary)"} : undefined}
 >
 <span
 className={`flex h-5 w-5 shrink-0 items-center justify-center ${
 active ?"": panelStyles.mutedText
 }`}
 style={active ? { color: "var(--color-primary)"} : undefined}
 >
 {item.icon}
 </span>
 {!collapsed && <span className="truncate">{item.label}</span>}
 {!collapsed && active && (
 <span
 className={`ml-auto h-1.5 w-1.5 rounded-full ${isDarkPanel ?"bg-white": meta.dotColor}`}
 />
 )}
 </button>
 );
 };

 const navContent = (
 <>
 {""}
 <div className="relative flex items-center justify-center px-3 py-3">
 <SidebarHeaderLogo compact={collapsed} />
 </div>
 <div className="flex-1 overflow-y-auto px-3 pb-2">
 {filteredSections.map((section, idx) => {
 const isCollapsible = section.collapsible === true;
 const sectionKey = section.title ||`section-${idx}`;
 const isSectionCollapsed =
 collapsedSections[sectionKey] ?? section.defaultCollapsed ?? false;

 const toggleSection = () => {
 setCollapsedSections((prev) => ({
 ...prev,
 [sectionKey]: !isSectionCollapsed,
 }));
 };

 return (
 <div key={sectionKey} className={idx === 0 ?"":"mt-4"}>
 {section.title &&
 (isCollapsible ? (
 <button
 type="button"onClick={toggleSection}
 className={`flex w-full items-center gap-1 px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider ${panelStyles.mutedText} hover:opacity-80 transition ${collapsed ?"hidden":""}`}
 >
 <svg
 className={`h-3 w-3 transition-transform ${isSectionCollapsed ?"":"rotate-90"}`}
 viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2.5"strokeLinecap="round"strokeLinejoin="round">
 <path d="M9 18l6-6-6-6"/>
 </svg>
 <span>{section.title}</span>
 </button>
 ) : (
 <p
 className={`px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider ${panelStyles.mutedText} ${collapsed ?"hidden":""}`}
 >
 {section.title}
 </p>
 ))}
 {(!isCollapsible || !isSectionCollapsed) && (
 <nav className="space-y-0.5">
 {section.items.map(renderItem)}
 </nav>
 )}
 </div>
 );
 })}
 </div>
 {/* Sign out removed — available via Profile page */}
 </>
 );

 // Compute sidebar CSS classes
 const sidebarBaseClasses =`app-sidebar ${panelStyles.bg} ${panelStyles.border}`;
 const desktopWidthClass = collapsed
 ?"app-sidebar--collapsed":"app-sidebar--expanded";
 const mobileOpenClass = mobileOpen ?"app-sidebar--mobile-open":"";

 return (
 <>
 {/* Mobile backdrop - clicking closes sidebar */}
 <div
 className={`app-backdrop ${mobileOpen ?"app-backdrop--visible":""} lg:hidden`}
 onClick={onMobileClose}
 aria-hidden="true"/>

 {/* Sidebar - unified for both desktop and mobile */}
 <aside
 className={`${sidebarBaseClasses} ${desktopWidthClass} ${mobileOpenClass}`}
 aria-label="Main navigation"role="navigation">
 {/* Close button for mobile */}
 <button
 type="button"onClick={onMobileClose}
 aria-label="Close menu"className="absolute right-3 top-3 rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 lg:hidden">
 <svg
 className="h-5 w-5"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M18 6L6 18M6 6l12 12"/>
 </svg>
 </button>

 {navContent}
 </aside>
 </>
 );
}
